import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputBase,
  Paper,
  Menu,
  MenuItem,
  Tooltip,
  Fab,
  useMediaQuery,
  useTheme,
  Box,
} from '@mui/material';

import {
  AttachFile,
  CheckCircleOutline,
  Clear,
  Comment,
  Description,
  HighlightOff,
  Mic,
  Mood,
  MoreVert,
  Send,
  PermMedia,
  Person,
  Reply,
  Duo,
  Add as AddIcon,
  Bolt as BoltIcon,
  CameraAlt,
  Menu as MenuIcon,
} from '@mui/icons-material';

import RecordingTimer from './RecordingTimer';
import MessageUploadMedias from '../MessageUploadMedias';
import ContactSendModal from '../ContactSendModal';
import CameraModal from '../CameraModal';
import ButtonModal from '../ButtonModal';

import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import MicRecorder from '@jmd01/mic-recorder-to-mp3';
import { ReplyMessageContext } from '../../context/ReplyingMessage/ReplyingMessageContext';
import { AuthContext } from '../../context/Auth/AuthContext';
import { i18n } from '../../translate/i18n';
import toastError from '../../toasts/toastError';
import api from '../../services/api';
import { isNil } from 'lodash';
import useQuickMessages from '../../hooks/useQuickMessages';
import { isString, isEmpty } from 'lodash';
import axios from 'axios';
import { ForwardMessageContext } from '../../context/ForwarMessage/ForwardMessageContext';
import { EditMessageContext } from '../../context/EditingMessage/EditingMessageContext';
import { getStyled } from './styles';
import { TicketsContext } from '../../context/Tickets/TicketsContext';

const MessageInput = ({
  ticket,
  ticketId,
  ticketStatus,
  droppedFiles,
  ticketChannel,
}) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const navigate = useNavigate();
  const [mediasUpload, setMediasUpload] = useState([]);
  const [buttonModalOpen, setButtonModalOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const [onDragEnter, setOnDragEnter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { setEditingMessage, editingMessage } = useContext(EditMessageContext);
  const { setTab, setTabOpen, setSearchContact } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const [privateMessage, setPrivateMessage] = useState(false);
  const [privateMessageInputVisible, setPrivateMessageInputVisible] =
    useState(false);
  const [senVcardModalOpen, setSenVcardModalOpen] = useState(false);
  const [showModalMedias, setShowModalMedias] = useState(false);
  const { list: listQuickMessages, cancelAll: cancelQuickMessages } =
    useQuickMessages();
  const isMobile = useMediaQuery('(maxWidth: 767px)');
  const [placeholderText, setPlaceHolderText] = useState('');
  const [hasCamera, setHasCamera] = useState(false);
  const [modalCameraOpen, setModalCameraOpen] = useState(false);

  const Mp3Recorder = useRef(new MicRecorder({ bitRate: 256 }));

  const isMounted = useRef(true);
  const quickMediaControllerRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (quickMediaControllerRef.current) {
        quickMediaControllerRef.current.abort();
      }
      cancelQuickMessages();
    };
  }, []); //eslint-disable-line

  const focusInput = useCallback(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  useEffect(() => {
    let text = '';
    switch (ticketStatus) {
      case 'open':
      case 'group':
        text = i18n.t('messagesInput.placeholderOpen');
        break;
      case 'spying':
        text = i18n.t('messagesInput.placeholderSpying');
        break;
      default:
        text = i18n.t('messagesInput.placeholderClosed');
    }
    if (isMobile && text.length > 20) {
      text = text.substring(0, 20) + '...';
    }
    setPlaceHolderText(text);
  }, [ticketStatus, isMobile]);

  useEffect(() => {
    const checkCamera = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setHasCamera(devices.some((d) => d.kind === 'videoinput'));
      } catch {
        setHasCamera(false);
      }
    };
    checkCamera();
  }, []);

  const {
    selectedMessages,
    setForwardMessageModalOpen,
    showSelectMessageCheckbox,
  } = useContext(ForwardMessageContext);

  useEffect(() => {
    if (droppedFiles && droppedFiles.length > 0) {
      setMediasUpload(Array.from(droppedFiles));
      setShowModalMedias(true);
    }
  }, [droppedFiles]);

  useEffect(() => {
    if (editingMessage) {
      setInputMessage(editingMessage.body);
    }
    focusInput();
  }, [replyingMessage, editingMessage, focusInput]);

  useEffect(() => {
    focusInput();
    return () => {
      setInputMessage('');
      setShowEmoji(false);
      setMediasUpload([]);
      setReplyingMessage(null);
      setPrivateMessage(false);
      setPrivateMessageInputVisible(false);
      setEditingMessage(null);
    };
  }, [ticketId, setReplyingMessage, setEditingMessage, focusInput]);

  useEffect(() => {
    if (onDragEnter === true) {
      setTimeout(() => setOnDragEnter(false), 1000);
    }
  }, [onDragEnter]);

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;
    if (!companyId || !userId) return;

    let cancelled = false;

    async function fetchData() {
      try {
        const messages = await listQuickMessages({ companyId, userId });
        if (cancelled || !isMounted.current) return;
        const options = messages.map((m) => {
          let truncatedMessage = m.message;
          if (isString(truncatedMessage) && truncatedMessage.length > 90) {
            truncatedMessage = m.message.substring(0, 90) + '...';
          }
          return {
            value: m.message,
            label: `/${m.shortcode}`,
            mediaPath: m.mediaPath,
          };
        });
        setQuickAnswer(options);
      } catch (err) {
        if (cancelled || !isMounted.current) return;
        toastError(err);
      }
    }
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [user.companyId, user.id]); //eslint-disable-line

  useEffect(() => {
    if (
      isString(inputMessage) &&
      !isEmpty(inputMessage) &&
      inputMessage.length >= 1
    ) {
      const firstWord = inputMessage.charAt(0);
      if (firstWord === '/') {
        const filteredOptions = quickAnswers.filter(
          (m) => m.label.toLowerCase().indexOf(inputMessage.toLowerCase()) > -1,
        );
        setTypeBar(filteredOptions);
      } else {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  }, [inputMessage, quickAnswers]);

  const handleSendLinkVideo = async () => {
    setInputMessage(`https://meet.jit.si/${ticketId}`);
    focusInput();
  };

  const handleChangeInput = (e) => setInputMessage(e.target.value);

  const handlePrivateMessage = () => {
    setPrivateMessage((prev) => !prev);
    setPrivateMessageInputVisible((prev) => !prev);
    focusInput();
  };

  const handleButtonModalOpen = () => {
    handleMenuItemClick();
    setButtonModalOpen(true);
  };

  const handleQuickAnswersClick = async (value) => {
    if (value.mediaPath) {
      if (quickMediaControllerRef.current) {
        quickMediaControllerRef.current.abort();
      }
      quickMediaControllerRef.current = new AbortController();
      try {
        const { data } = await axios.get(value.mediaPath, {
          responseType: 'blob',
          signal: quickMediaControllerRef.current.signal,
        });
        if (!isMounted.current) return;
        handleUploadQuickMessageMedia(data, value.value);
        setInputMessage('');
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        toastError(err);
      } finally {
        quickMediaControllerRef.current = null;
        focusInput();
      }
      return;
    }

    setInputMessage(value.value);
    setTypeBar(false);
    focusInput();
  };

  const handleAddEmoji = (e) => {
    setInputMessage((prev) => prev + e.native);
    focusInput();
  };

  const handleCapture = (imageData) => {
    if (imageData) handleUploadCamera(imageData);
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) return;
    setMediasUpload(Array.from(e.target.files));
    setShowModalMedias(true);
  };

  const handleOpenModalForward = () => {
    if (selectedMessages.length === 0) {
      setForwardMessageModalOpen(false);
      toastError(i18n.t('messagesList.header.notMessage'));
      return;
    }
    setForwardMessageModalOpen(true);
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      setMediasUpload(Array.from(e.clipboardData.files));
      setShowModalMedias(true);
    }
  };

  const handleInputDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) {
      setMediasUpload(Array.from(e.dataTransfer.files));
      setShowModalMedias(true);
    }
  };

  const handleCloseCameraModal = useCallback(() => {
    setModalCameraOpen(false);
    focusInput();
  }, [focusInput]);

  const handleUploadMedia = async (mediasUpload) => {
    setLoading(true);
    if (!mediasUpload.length) {
      setLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append('fromMe', true);
    formData.append('isPrivate', privateMessage ? 'true' : 'false');
    mediasUpload.forEach((media) => {
      formData.append('body', media.caption);
      formData.append('medias', media.file);
    });
    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setMediasUpload([]);
      setShowModalMedias(false);
      setPrivateMessage(false);
      setPrivateMessageInputVisible(false);
      focusInput();
    }
  };

  const handleSendContatcMessage = async (vcard) => {
    setSenVcardModalOpen(false);
    setLoading(true);
    if (isNil(vcard)) {
      setLoading(false);
      return;
    }
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: '',
      body: null,
      quotedMsg: replyingMessage,
      isPrivate: privateMessage ? 'true' : 'false',
      vCard: vcard,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    } finally {
      setInputMessage('');
      setShowEmoji(false);
      setLoading(false);
      setReplyingMessage(null);
      setEditingMessage(null);
      setPrivateMessage(false);
      setPrivateMessageInputVisible(false);
      focusInput();
    }
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === '') return;
    setLoading(true);

    if (ticketStatus === 'spying' || ticketStatus === 'pending') {
      try {
        const otherTicket = await api.put(`/tickets/${ticketId}`, {
          status: 'open',
          userId: user.id,
          queueId: user.queues[0].id,
        });

        if (otherTicket.data.id !== ticket.id) {
          navigate(`/tickets/${otherTicket.data.uuid}`);
          setTab('open');
          setTabOpen('open');
          setSearchContact('');
        } else {
          navigate(`/tickets/${ticket.uuid}`);
          setTab('open');
          setTabOpen('open');
        }
      } catch (err) {
        toastError(err);
      }
    }

    const userName = privateMessage
      ? `${user.name} - Mensagem Privada`
      : user.name;
    const sendMessage = inputMessage.trim();
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: '',
      body: !editingMessage ? `*${userName}:*\n${sendMessage}` : sendMessage,
      quotedMsg: replyingMessage,
      isPrivate: privateMessage ? 'true' : 'false',
    };

    try {
      if (editingMessage !== null) {
        await api.post(`/messages/edit/${editingMessage.id}`, message);
      } else {
        await api.post(`/messages/${ticketId}`, message);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setInputMessage('');
      setShowEmoji(false);
      setLoading(false);
      setReplyingMessage(null);
      setPrivateMessage(false);
      setEditingMessage(null);
      setPrivateMessageInputVisible(false);
      handleMenuItemClick();
      focusInput();
    }
  };

  const handleOpenTicket = async () => {
    try {
      const otherTicket = await api.put(`/tickets/${ticketId}`, {
        status: 'open',
        userId: user.id,
        queueId: user.queues[0].id,
      });
      if (otherTicket.data.id !== ticket.id) {
        navigate(`/tickets/${otherTicket.data.uuid}`);
      } else {
        navigate(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await Mp3Recorder.current.start();
      setRecording(true);
    } catch (e) {
      toastError(
        `ERRO AO GRAVAR AUDIO! VERIFIQUE SE HÁ UM MICROFONE CONECTADO. \nErro: ${e.error}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCamera = async (blob) => {
    setLoading(true);
    try {
      const formData = new FormData();
      const filename = `${new Date().getTime()}.png`;
      formData.append('medias', blob, filename);
      formData.append('body', privateMessage ? `\u200d` : '');
      formData.append('fromMe', true);
      if (ticketStatus === 'spying' || ticketStatus === 'pending') {
        await handleOpenTicket();
      }
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      focusInput();
    }
  };

  const handleUploadQuickMessageMedia = async (blob, message) => {
    setLoading(true);
    try {
      const extension = blob.type.split('/')[1];
      const formData = new FormData();
      const filename = `${new Date().getTime()}.${extension}`;
      formData.append('medias', blob, filename);
      formData.append('body', privateMessage ? `\u200d${message}` : message);
      formData.append('fromMe', true);
      if (ticketStatus === 'spying' || ticketStatus === 'pending') {
        await handleOpenTicket();
      }
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const [, blob] = await Mp3Recorder.current.stop().getMp3();
      if (blob.size < 10000) {
        setRecording(false);
        return;
      }
      const formData = new FormData();
      const filename =
        ticketChannel === 'whatsapp'
          ? `${new Date().getTime()}.mp3`
          : `${new Date().getTime()}.m4a`;
      formData.append('medias', blob, filename);
      formData.append('body', filename);
      formData.append('fromMe', true);
      if (replyingMessage) {
        formData.append('quotedMsg', JSON.stringify(replyingMessage));
      }
      if (ticketStatus === 'spying' || ticketStatus === 'pending') {
        await handleOpenTicket();
      }
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setReplyingMessage(null);
      setRecording(false);
      focusInput();
    }
  };

  const handleCloseModalMedias = () => {
    setShowModalMedias(false);
    focusInput();
  };

  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.current.stop().getMp3();
      setRecording(false);
      focusInput();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = (event) => setAnchorEl(event.currentTarget);

  const handleMenuItemClick = () => {
    setAnchorEl(null);
    focusInput();
  };

  const handleSendContactModalOpen = () => {
    handleMenuItemClick();
    setSenVcardModalOpen(true);
  };

  const handleCameraModalOpen = () => {
    handleMenuItemClick();
    setModalCameraOpen(true);
  };

  const handleCancelSelection = () => {
    setMediasUpload([]);
    setShowModalMedias(false);
    focusInput();
  };

  const disableOption = () => {
    return (
      loading ||
      recording ||
      (ticketStatus !== 'open' &&
        ticketStatus !== 'group' &&
        ticketStatus !== 'spying' &&
        ticketStatus !== 'pending')
    );
  };

  const renderReplyingMessage = (message) => (
    <div style={classes.replyginMsgWrapper}>
      <div style={classes.replyginMsgContainer}>
        <span
          style={{
            ...classes.replyginContactMsgSideColor,
            ...(!message.fromMe && classes.replyginSelfMsgSideColor),
          }}
        />
        {replyingMessage && (
          <div style={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span style={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        )}
      </div>
      <IconButton
        aria-label='showRecorder'
        component='span'
        onClick={() => {
          setReplyingMessage(null);
          setEditingMessage(null);
          setInputMessage('');
          focusInput();
        }}
        sx={{
          '&:hover': {
            backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
          },
        }}
      >
        <Clear sx={classes.sendMessageIcons} />
      </IconButton>
    </div>
  );

  if (mediasUpload.length > 0) {
    return (
      <Paper
        elevation={0}
        square
        style={classes.viewMediaInputWrapper}
        onDragEnter={() => setOnDragEnter(true)}
        onDrop={handleInputDrop}
      >
        {showModalMedias && (
          <MessageUploadMedias
            isOpen={showModalMedias}
            files={mediasUpload}
            onClose={handleCloseModalMedias}
            onSend={handleUploadMedia}
            onCancelSelection={handleCancelSelection}
          />
        )}
      </Paper>
    );
  }

  return (
    <>
      <CameraModal
        isOpen={modalCameraOpen}
        onRequestClose={handleCloseCameraModal}
        onCapture={handleCapture}
      />
      {senVcardModalOpen && (
        <ContactSendModal
          modalOpen={senVcardModalOpen}
          onClose={(c) => handleSendContatcMessage(c)}
        />
      )}
      <Paper
        square
        elevation={0}
        style={classes.mainWrapper}
        onDragEnter={() => setOnDragEnter(true)}
        onDrop={handleInputDrop}
      >
        {(replyingMessage && renderReplyingMessage(replyingMessage)) ||
          (editingMessage && renderReplyingMessage(editingMessage))}
        <div style={classes.newMessageBox}>
          {!isMobile && (
            <>
              <IconButton
                aria-label='emojiPicker'
                component='span'
                disabled={disableOption()}
                onClick={() => setShowEmoji((prev) => !prev)}
                sx={{
                  '&:hover': {
                    backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.1'})`,
                  },
                }}
              >
                <Mood sx={classes.sendMessageIcons} />
              </IconButton>
              {showEmoji && (
                <ClickAwayListener
                  onClickAway={() => {
                    setShowEmoji(false);
                    focusInput();
                  }}
                >
                  <div style={classes.emojiBox}>
                    <Picker
                      data={data}
                      perLine={16}
                      theme='dark'
                      showPreview
                      showSkinTones={false}
                      onEmojiSelect={handleAddEmoji}
                    />
                  </div>
                </ClickAwayListener>
              )}

              <Fab
                disabled={disableOption()}
                aria-label='uploadMedias'
                component='span'
                sx={classes.invertedFabMenu}
                onClick={handleOpenMenuClick}
              >
                <IconButton
                  sx={{
                    padding: 1,
                    color: 'grey',
                    '&:hover': {
                      borderRadius: 50,
                      backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Fab>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleMenuItemClick}
                id='simple-menu'
                {...theme.custom.menuProps}
              >
                <MenuItem onClick={handleMenuItemClick}>
                  <input
                    multiple
                    type='file'
                    id='upload-img-button'
                    accept='image/*, video/*, audio/*'
                    style={classes.uploadInput}
                    onChange={handleChangeMedias}
                  />
                  <label htmlFor='upload-img-button'>
                    <Fab
                      aria-label='upload-img'
                      component='span'
                      sx={classes.invertedFabMenuMP}
                    >
                      <PermMedia />
                    </Fab>
                    {i18n.t('messageInput.type.imageVideo')}
                  </label>
                </MenuItem>
                {hasCamera && (
                  <MenuItem onClick={handleCameraModalOpen}>
                    <Fab style={classes.invertedFabMenuCamera}>
                      <CameraAlt />
                    </Fab>
                    {i18n.t('messageInput.type.cam')}
                  </MenuItem>
                )}
                <MenuItem onClick={handleMenuItemClick}>
                  <input
                    multiple
                    type='file'
                    id='upload-doc-button'
                    accept='application/*, text/*'
                    style={classes.uploadInput}
                    onChange={handleChangeMedias}
                  />
                  <label htmlFor='upload-doc-button'>
                    <Fab
                      aria-label='upload-doc'
                      component='span'
                      style={classes.invertedFabMenuDoc}
                    >
                      <Description />
                    </Fab>
                    Documento
                  </label>
                </MenuItem>
                <MenuItem onClick={handleSendContactModalOpen}>
                  <Fab style={classes.invertedFabMenuCont}>
                    <Person />
                  </Fab>
                  {i18n.t('messageInput.type.contact')}
                </MenuItem>
                <MenuItem onClick={handleSendLinkVideo}>
                  <Fab style={classes.invertedFabMenuMeet}>
                    <Duo />
                  </Fab>
                  {i18n.t('messageInput.type.meet')}
                </MenuItem>
                {buttonModalOpen && (
                  <ButtonModal
                    modalOpen={buttonModalOpen}
                    onClose={() => {
                      setButtonModalOpen(false);
                      focusInput();
                    }}
                    ticketId={ticketId}
                  />
                )}
                <MenuItem onClick={handleButtonModalOpen}>
                  <Fab style={classes.invertedFabMenuCont}>
                    <MenuIcon />
                  </Fab>
                  Botões
                </MenuItem>
              </Menu>
            </>
          )}

          {isMobile && (
            <>
              <IconButton
                aria-controls='simple-menu'
                aria-haspopup='true'
                onClick={handleOpenMenuClick}
              >
                <MoreVert />
              </IconButton>
              <Menu
                id='simple-menu'
                keepMounted
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuItemClick}
              >
                <MenuItem onClick={handleMenuItemClick}>
                  <IconButton
                    aria-label='emojiPicker'
                    component='span'
                    disabled={disableOption()}
                    onClick={() => setShowEmoji((prev) => !prev)}
                  >
                    <Mood style={classes.sendMessageIcons} />
                  </IconButton>
                </MenuItem>
                <MenuItem onClick={handleMenuItemClick}>
                  <input
                    multiple
                    type='file'
                    id='upload-button'
                    disabled={disableOption()}
                    style={classes.uploadInput}
                    onChange={handleChangeMedias}
                  />
                  <label htmlFor='upload-button'>
                    <IconButton
                      aria-label='upload'
                      component='span'
                      disabled={disableOption()}
                    >
                      <AttachFile style={classes.sendMessageIcons} />
                    </IconButton>
                  </label>
                </MenuItem>
                <Tooltip title='Habilitar/Desabilitar Comentários'>
                  <IconButton
                    aria-label='send-upload'
                    component='span'
                    onClick={handlePrivateMessage}
                  >
                    <Comment
                      style={{
                        color: privateMessage
                          ? theme.palette.mode === 'light'
                            ? theme.palette.primary.main
                            : '#EEE'
                          : 'grey',
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Menu>
            </>
          )}

          <div style={classes.flexContainer}>
            {privateMessageInputVisible && (
              <div style={classes.flexItem}>
                <div style={classes.messageInputWrapperPrivate}>
                  <InputBase
                    inputRef={inputRef}
                    style={classes.messageInputPrivate}
                    placeholder={
                      ticketStatus === 'open' || ticketStatus === 'group'
                        ? i18n.t('messagesInput.placeholderPrivateMessage')
                        : i18n.t('messagesInput.placeholderClosed')
                    }
                    multiline
                    maxRows={5}
                    value={inputMessage}
                    onChange={handleChangeInput}
                    disabled={disableOption()}
                    onPaste={(e) => {
                      if (ticketStatus === 'open' || ticketStatus === 'group')
                        handleInputPaste(e);
                    }}
                    onKeyPress={(e) => {
                      if (loading || e.shiftKey) return;
                      if (e.key === 'Enter') handleSendMessage();
                    }}
                  />
                  {typeBar && (
                    <ul style={classes.messageQuickAnswersWrapper}>
                      {typeBar.map((value, index) => (
                        <li
                          style={classes.messageQuickAnswersWrapperItem}
                          key={index}
                        >
                          <a onClick={() => handleQuickAnswersClick(value)}>
                            {`${value.label} - ${value.value}`}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
            {!privateMessageInputVisible && (
              <div style={classes.flexItem}>
                <div style={classes.messageInputWrapper}>
                  <InputBase
                    inputRef={inputRef}
                    style={classes.messageInput}
                    placeholder={placeholderText}
                    multiline
                    maxRows={5}
                    value={inputMessage}
                    onChange={handleChangeInput}
                    disabled={disableOption()}
                    onPaste={(e) => {
                      if (ticketStatus === 'open' || ticketStatus === 'group')
                        handleInputPaste(e);
                    }}
                    onKeyPress={(e) => {
                      if (loading || e.shiftKey) return;
                      if (e.key === 'Enter') handleSendMessage();
                    }}
                  />
                  {typeBar && (
                    <ul style={classes.messageQuickAnswersWrapper}>
                      {typeBar.map((value, index) => (
                        <li
                          style={classes.messageQuickAnswersWrapperItem}
                          key={index}
                        >
                          <Box
                            component='a'
                            onClick={() => handleQuickAnswersClick(value)}
                            sx={classes.messageQuickAnswersWrapperLink}
                          >
                            {`${value.label} - ${value.value}`}
                          </Box>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {!privateMessageInputVisible && (
            <>
              <Tooltip title={i18n.t('tickets.buttons.quickmessageflash')}>
                <IconButton
                  aria-label='flash'
                  component='span'
                  onClick={() => {
                    setInputMessage('/');
                    focusInput();
                  }}
                  sx={{
                    '&:hover': {
                      borderRadius: 50,
                      backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
                    },
                  }}
                >
                  <BoltIcon style={classes.sendMessageIcons} />
                </IconButton>
              </Tooltip>
              {inputMessage || showSelectMessageCheckbox ? (
                <IconButton
                  aria-label='sendMessage'
                  component='span'
                  onClick={
                    showSelectMessageCheckbox
                      ? handleOpenModalForward
                      : handleSendMessage
                  }
                  disabled={loading}
                  sx={{
                    '&:hover': {
                      borderRadius: 50,
                      backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
                    },
                  }}
                >
                  {showSelectMessageCheckbox ? (
                    <Reply sx={classes.ForwardMessageIcons} />
                  ) : (
                    <Send sx={classes.sendMessageIcons} />
                  )}
                </IconButton>
              ) : recording ? (
                <div style={classes.recorderWrapper}>
                  <IconButton
                    aria-label='cancelRecording'
                    component='span'
                    disabled={loading}
                    onClick={handleCancelAudio}
                    sx={{
                      '&:hover': {
                        borderRadius: 50,
                        backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
                      },
                    }}
                  >
                    <HighlightOff sx={classes.cancelAudioIcon} />
                  </IconButton>
                  {loading ? (
                    <div>
                      <CircularProgress sx={classes.audioLoading} />
                    </div>
                  ) : (
                    <RecordingTimer sx={{ color: theme.custom.colors.text }} />
                  )}
                  <IconButton
                    aria-label='sendRecordedAudio'
                    component='span'
                    onClick={handleUploadAudio}
                    disabled={loading}
                    sx={{
                      '&:hover': {
                        borderRadius: 50,
                        backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
                      },
                    }}
                  >
                    <CheckCircleOutline sx={classes.sendAudioIcon} />
                  </IconButton>
                </div>
              ) : (
                <IconButton
                  aria-label='showRecorder'
                  component='span'
                  disabled={disableOption()}
                  onClick={handleStartRecording}
                  sx={{
                    '&:hover': {
                      borderRadius: 50,
                      backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
                    },
                  }}
                >
                  <Mic sx={classes.sendMessageIcons} />
                </IconButton>
              )}
            </>
          )}

          {privateMessageInputVisible && (
            <IconButton
              aria-label='sendMessage'
              component='span'
              onClick={
                showSelectMessageCheckbox
                  ? handleOpenModalForward
                  : handleSendMessage
              }
              disabled={loading}
              sx={{
                '&:hover': {
                  borderRadius: 50,
                  backgroundColor: `rgb(221, 221, 221, ${theme.palette.mode === 'light' ? '0.7' : '0.2'})`,
                },
              }}
            >
              {showSelectMessageCheckbox ? (
                <Reply sx={classes.ForwardMessageIcons} />
              ) : (
                <Send sx={classes.sendMessageIcons} />
              )}
            </IconButton>
          )}
        </div>
      </Paper>
    </>
  );
};

export default MessageInput;
